import React from "react";

import { ChartColumn, File, Pencil, Trash2, UserRound } from "lucide-react";

import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  countColumn,
  dateColumn,
  statusColumn,
} from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { offerEditPath, PROJECTS_ROUTE } from "../../routes/routes";

import type { TFunction } from "i18next";

import type { OfferRow } from "./offerColumns.types";
import type { DataTableColumnDef } from "../../dataTable/dataTable.types";

interface OfferColumnsOptions {
  t: TFunction;
  projectSlug: string;
  offerCount: number;
  canWrite: boolean;
  canViewStats: boolean;
  onEdit: (offer: OfferRow) => void;
  onViewStats: (offer: OfferRow) => void;
  onDelete: (offer: OfferRow) => void;
}

export function getOfferEditHref(
  projectSlug: string | undefined,
  uuid: string,
): string {
  return (
    offerEditPath(projectSlug, uuid) ??
    `${PROJECTS_ROUTE}/${projectSlug}/offers`
  );
}

export function offerColumns({
  t,
  projectSlug,
  offerCount,
  canWrite,
  canViewStats,
  onEdit,
  onViewStats,
  onDelete,
}: OfferColumnsOptions): DataTableColumnDef<OfferRow>[] {
  return [
    statusColumn<OfferRow>("status", t("status"), [
      { value: "active", label: t("table:status.active"), tone: "success" },
      { value: "stop", label: t("table:status.stop"), tone: "error" },
    ]),
    {
      accessorKey: "label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      meta: { searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={
            canWrite
              ? getOfferEditHref(projectSlug, row.original.uuid)
              : undefined
          }
        >
          {row.original.label}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "rank",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("rank")} />
      ),
      meta: {
        label: t("rank"),
        width: "15%",
        minWidth: 185,
        align: "center",
      },
      cell: ({ row }) => `${row.original.rank}/${offerCount}`,
    },
    countColumn<OfferRow>("medias_count", t("medias_count"), <File />),
    countColumn<OfferRow>(
      "subscribers_count",
      t("active_subscribers"),
      <UserRound />,
      { width: "11%", minWidth: 135 },
    ),
    dateColumn<OfferRow>("updated_at", t("update_date")),
    actionsColumn<OfferRow>(
      (offer) => [
        {
          label: t("viewOfferStats"),
          icon: <ChartColumn />,
          hidden: !canViewStats,
          onClick: () => onViewStats(offer),
        },
        {
          label: t("edit"),
          icon: <Pencil />,
          hidden: !canWrite,
          onClick: () => onEdit(offer),
        },
        {
          label: t("delete"),
          icon: <Trash2 />,
          variant: "destructive",
          hidden: !canWrite,
          // Une offre avec des abonnés actifs ne se supprime pas.
          disabled: !offer.deletable,
          tooltip: offer.deletable ? undefined : t("deleteDisabled"),
          onClick: () => onDelete(offer),
          confirm: {
            title: t("deleteOfferTitle"),
            description: t("deleteOfferBody", { offer: offer.label }),
            confirmLabel: t("deleteConfirm"),
          },
        },
      ],
      (offer) => offer.label,
    ),
  ];
}
